// Slack channel sources (docs/Y2026-Q4/design-slack-channel-sources.md).
//
// Mirrors src/inrange_agents/lambda_handlers/api/slack/*. One request per card or
// page: a list carries everything its rows render.
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import { QK, queryKeys } from "../queryKeys";

export interface SlackChannel {
  channel_id: string;
  name: string;
  is_private: boolean;
  is_ext_shared: boolean;
  is_archived: boolean;
  bot_is_member: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  backfill_complete: boolean;
  history_from: string | null;
}

export interface SlackWorkspace {
  // "@InRange Agents (QA)" and the like: each environment has its own app.
  bot_mention: string;
  team_id: string;
  team_name: string;
  team_domain: string | null;
  status: string;
  last_error: string | null;
}

export interface SlackSubscription {
  subscription_id: string;
  // Null for a tenant / landlord-scoped subscription.
  project_id: string | null;
  tenant_scope: string | null;
  landlord_scope: string | null;
  created_at: string;
  created_by_email: string | null;
  // How many projects see this channel, through any subscription.
  project_count: number;
  channel: SlackChannel;
}

export interface SlackFile {
  file_id: string;
  name: string;
  mimetype: string;
  size_bytes: number | null;
  voice_note: boolean;
  transcript_status: string | null;
  transcript: string | null;
}

export interface SlackMessage {
  ts: string;
  posted_at: string;
  day: string;
  author: string;
  author_email: string | null;
  author_external: boolean;
  text: string;
  deleted: boolean;
  edited: boolean;
  hidden: boolean;
  thread_ts: string | null;
  is_reply: boolean;
  reply_count: number;
  parent_author: string | null;
  parent_excerpt: string | null;
  files: SlackFile[];
  permalink: string | null;
}

// A sync request is staged on the workspace lane, or skipped with words saying why.
export interface SlackStageResponse {
  operation_key: string | null;
  skip_reason: string | null;
  message?: string;
}

interface StatusResponse {
  configured: boolean;
  workspace: SlackWorkspace | null;
  channels: SlackChannel[];
}

interface SubscriptionsResponse {
  configured: boolean;
  // The workspace's sync in flight, so a card opened mid-sync shows its progress.
  active_operation_key?: string | null;
  workspace: SlackWorkspace | null;
  subscriptions: SlackSubscription[];
}

export interface SlackDayDocument {
  global_data_id: string;
  title: string;
  channel: SlackChannel;
  day: string;
  messages: SlackMessage[];
}

export const useSlackStatus = (enabled = true) => {
  const http = useAgentsHttp();
  return useQuery<StatusResponse>(
    [QK.slackStatus],
    () => http<StatusResponse>("/slack/status", {}),
    { enabled, refetchOnWindowFocus: false },
  );
};

export interface SlackChannelListResponse extends StatusResponse {
  // Why Slack could not be asked just now; the channels are then the last known list.
  refresh_error: string | null;
}

// The channel picker's data. It asks Slack for the channel list each time it opens, and
// again when the window regains focus: the usual reason to come back is having just
// invited the bot in Slack.
export const useSlackChannelPicker = (open: boolean) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  const status = useSlackStatus(open);
  const refresh = useMutation<SlackChannelListResponse, Error, void>(
    () => http<SlackChannelListResponse>("/slack/channels/refresh", {}),
    {
      onSuccess: async (data) => {
        // A status fetch started with the picker must not land after, and overwrite, this.
        await qc.cancelQueries([QK.slackStatus]);
        qc.setQueryData([QK.slackStatus], data);
      },
    },
  );
  const { mutate } = refresh;
  const inFlight = useRef(false);
  useEffect(() => {
    if (!open) return undefined;
    const check = () => {
      if (inFlight.current) return;
      inFlight.current = true;
      mutate(undefined, {
        onSettled: () => {
          inFlight.current = false;
        },
      });
    };
    check();
    window.addEventListener("focus", check);
    return () => window.removeEventListener("focus", check);
  }, [open, mutate]);
  return { status, refresh };
};

export const useProjectSlack = (projectId: string) => {
  const http = useAgentsHttp();
  return useQuery<SubscriptionsResponse>(
    queryKeys.projectSlack(projectId || null),
    () => http<SubscriptionsResponse>("/projects/slack/list", { project_id: projectId }),
    { enabled: Boolean(projectId), refetchOnWindowFocus: false },
  );
};

// Every list a subscription change can move: the project's, every other project's
// (a scope subscription reaches several) and the scoped list.
const useInvalidateSlackLists = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries([QK.projectSlack]);
    void qc.invalidateQueries([QK.slackScopeSubscriptions]);
  };
};

export const useSubscribeProjectSlack = (projectId: string) => {
  const http = useAgentsHttp();
  const invalidate = useInvalidateSlackLists();
  return useMutation<SlackStageResponse & { subscription_id: string }, Error, string>(
    (channelId) =>
      http("/projects/slack/subscribe", { project_id: projectId, channel_id: channelId }),
    { onSuccess: invalidate },
  );
};

export const useUnsubscribeSlack = () => {
  const http = useAgentsHttp();
  const invalidate = useInvalidateSlackLists();
  return useMutation<unknown, Error, string>(
    (subscriptionId) => http("/slack/unsubscribe", { subscription_id: subscriptionId }),
    { onSuccess: invalidate },
  );
};

export const useSyncProjectSlackNow = (projectId: string) => {
  const http = useAgentsHttp();
  return useMutation<SlackStageResponse, Error, void>(() =>
    http<SlackStageResponse>("/projects/slack/sync-now", { project_id: projectId }),
  );
};

export const useSyncScopedSlackNow = () => {
  const http = useAgentsHttp();
  return useMutation<SlackStageResponse, Error, void>(() =>
    http<SlackStageResponse>("/slack/scope-sync-now", {}),
  );
};

export const useSlackScopeSubscriptions = () => {
  const http = useAgentsHttp();
  return useQuery<SubscriptionsResponse>(
    [QK.slackScopeSubscriptions],
    () => http<SubscriptionsResponse>("/slack/scope-subscriptions/list", {}),
    { refetchOnWindowFocus: false },
  );
};

export const useScopeSubscribeSlack = () => {
  const http = useAgentsHttp();
  const invalidate = useInvalidateSlackLists();
  return useMutation<
    SlackStageResponse & { subscription_id: string },
    Error,
    { channelId: string; tenantScope: string | null; landlordScope: string | null }
  >(
    ({ channelId, tenantScope, landlordScope }) =>
      http("/slack/scope-subscribe", {
        channel_id: channelId,
        tenant_scope: tenantScope,
        landlord_scope: landlordScope,
      }),
    { onSuccess: invalidate },
  );
};

export const useRescopeSlack = () => {
  const http = useAgentsHttp();
  const invalidate = useInvalidateSlackLists();
  return useMutation<
    unknown,
    Error,
    { subscriptionId: string; tenantScope: string | null; landlordScope: string | null }
  >(
    ({ subscriptionId, tenantScope, landlordScope }) =>
      http("/slack/scope-update", {
        subscription_id: subscriptionId,
        tenant_scope: tenantScope,
        landlord_scope: landlordScope,
      }),
    { onSuccess: invalidate },
  );
};

export const useSlackDayDocument = (globalDataId: string | null) => {
  const http = useAgentsHttp();
  return useQuery<SlackDayDocument>(
    queryKeys.slackDayDocument(globalDataId),
    () => http<SlackDayDocument>("/slack/day-document", { global_data_id: globalDataId }),
    { enabled: Boolean(globalDataId), refetchOnWindowFocus: false },
  );
};
